import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NOTE_DISPLAY_MAX_LENGTH } from '../../constants/share';
import './ShareCard.css';

/**
 * 개별 공유 항목을 표시하는 카드 컴포넌트
 * @param {Object} props - 컴포넌트 props
 * @param {Object} props.shareData - 공유 데이터
 * @param {Function} props.onRevoke - 공유 취소 핸들러
 * @param {string} props.passwordTitle - 패스워드 제목
 */
const ShareCard = ({ shareData, onRevoke, passwordTitle }) => {
  const [isRevoking, setIsRevoking] = useState(false);
  const [showFullNote, setShowFullNote] = useState(false);

  // 🔍 디버깅: prop으로 전달받은 데이터 확인
  console.log('ShareCard - shareData:', shareData);
  console.log('ShareCard - passwordTitle prop:', passwordTitle);
  console.log('ShareCard - shareNote:', shareData.shareNote);

  // 시간 계산 함수
  const getTimeRemaining = () => {
    const now = new Date();
    const expiresAt = shareData.expiresAt.toDate();
    const diff = expiresAt - now;

    if (diff <= 0) return '만료됨';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}일 ${hours}시간 후`;
    } else if (hours > 0) {
      return `${hours}시간 ${minutes}분 후`;
    } else {
      return `${minutes}분 후`;
    }
  };

  // ✨ 긴 메모 텍스트 처리
  const getDisplayNote = () => {
    if (!shareData.shareNote) return null;
    
    const note = shareData.shareNote.trim();
    if (note.length <= NOTE_DISPLAY_MAX_LENGTH) {
      return note;
    }
    
    if (showFullNote) {
      return note;
    }
    
    return `${note.substring(0, NOTE_DISPLAY_MAX_LENGTH)}...`;
  };

  const shouldShowToggle = () => {
    return shareData.shareNote && shareData.shareNote.length > NOTE_DISPLAY_MAX_LENGTH;
  };

  // 사용률 계산
  const usagePercentage = (shareData.currentUses / shareData.maxUses) * 100;
  
  // 상태에 따른 색상 클래스
  const getStatusClass = () => {
    if (shareData.currentUses >= shareData.maxUses) return 'share-card--completed';
    if (usagePercentage >= 80) return 'share-card--warning';
    return 'share-card--active';
  };

  // 공유 취소 핸들러
  const handleRevoke = async () => {
    if (window.confirm('이 공유를 취소하시겠습니까?')) {
      setIsRevoking(true);
      try {
        await onRevoke(shareData.accessToken);
      } catch (error) {
        console.error('공유 취소 실패:', error);
        alert('공유 취소에 실패했습니다.');
      } finally {
        setIsRevoking(false);
      }
    }
  };

  // 공유 링크 복사
  const copyShareLink = () => {
    const baseURL = window.location.origin;
    // accessToken을 URL에 사용 (shareKey가 아님)
    const shareURL = `${baseURL}/shared/${shareData.accessToken}`;
    
    navigator.clipboard.writeText(shareURL).then(() => {
      alert('공유 링크가 복사되었습니다!');
    }).catch(() => {
      alert('링크 복사에 실패했습니다.');
    });
  };

  // 짧은 액세스 토큰 표시
  const getShortAccessToken = () => {
    const token = shareData.accessToken || shareData.shareKey; // 하위 호환성
    return token.length > 20 
      ? `${token.substring(0, 12)}...${token.slice(-6)}`
      : token;
  };

  // 메모 토글 핸들러
  const toggleNoteDisplay = () => {
    setShowFullNote(prev => !prev);
  };

  const isCompleted = shareData.currentUses >= shareData.maxUses;
  const isExpired = new Date() > shareData.expiresAt.toDate();
  const displayNote = getDisplayNote();

  return (
    <div className={`share-card ${getStatusClass()}`}>
      <div className="share-card__header">
        <div className="share-card__title-section">
          <h4 className="share-card__title">
            🔗 {passwordTitle || shareData.passwordTitle || shareData.title || '패스워드'}
          </h4>
          {shareData.passwordWebsite && (
            <p className="share-card__website">
              🌐 {shareData.passwordWebsite}
            </p>
          )}
          {/* ✨ 개선된 공유 메모 표시 */}
          {displayNote && (
            <div className="share-card__note-container">
              <p className="share-card__note">
                📝 {displayNote}
              </p>
              {shouldShowToggle() && (
                <button 
                  className="share-card__note-toggle"
                  onClick={toggleNoteDisplay}
                  type="button"
                >
                  {showFullNote ? '간략히' : '전체보기'}
                </button>
              )}
            </div>
          )}
        </div>
        <div className="share-card__status">
          {isCompleted ? '완료' : isExpired ? '만료' : '활성'}
        </div>
      </div>

      <div className="share-card__info">
        <div className="share-card__key" onClick={copyShareLink} title="클릭하여 복사">
          <span className="share-card__key-label">액세스 코드:</span>
          <code className="share-card__key-value">{getShortAccessToken()}</code>
          <span className="share-card__copy-icon">📋</span>
        </div>
      </div>

      <div className="share-card__usage">
        <div className="share-card__usage-info">
          <span>사용률:</span>
          <span className="share-card__usage-text">
            {shareData.currentUses}/{shareData.maxUses}회
          </span>
        </div>
        <div className="share-card__progress">
          <div 
            className="share-card__progress-bar"
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          ></div>
        </div>
      </div>

      <div className="share-card__timing">
        <div className="share-card__timing-item">
          <span className="share-card__timing-label">생성:</span>
          <span className="share-card__timing-value">
            {shareData.createdAt.toDate().toLocaleDateString()}
          </span>
        </div>
        <div className="share-card__timing-item">
          <span className="share-card__timing-label">만료:</span>
          <span className={`share-card__timing-value ${isExpired ? 'expired' : ''}`}>
            {getTimeRemaining()}
          </span>
        </div>
      </div>

      <div className="share-card__actions">
        {!isCompleted && !isExpired && (
          <button
            className="share-card__revoke-btn"
            onClick={handleRevoke}
            disabled={isRevoking}
          >
            {isRevoking ? '취소 중...' : '🗑️ 취소'}
          </button>
        )}
        
        <button
          className="share-card__copy-btn"
          onClick={copyShareLink}
        >
          📋 링크 복사
        </button>
        
        {(isCompleted || isExpired) && (
          <span className="share-card__completed-badge">
            {isCompleted ? '✓ 완료' : '⏰ 만료'}
          </span>
        )}
      </div>
    </div>
  );
};

// ✨ PropTypes 정의
ShareCard.propTypes = {
  shareData: PropTypes.shape({
    shareKey: PropTypes.string.isRequired,
    passwordTitle: PropTypes.string,
    passwordWebsite: PropTypes.string,
    shareNote: PropTypes.string,
    createdAt: PropTypes.object.isRequired,
    expiresAt: PropTypes.object.isRequired,
    maxUses: PropTypes.number.isRequired,
    currentUses: PropTypes.number.isRequired,
    remainingUses: PropTypes.number
  }).isRequired,
  onRevoke: PropTypes.func.isRequired,
  passwordTitle: PropTypes.string
};

// 기본값 정의
ShareCard.defaultProps = {
  passwordTitle: null
};

export default ShareCard;